import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import * as Styled from 'shiftNotes/styles/EntityTag.style';

export interface EntityTagPopoverProps {
  title: string;
  description?: string | null;
  anchorEl: HTMLElement | null;
  open: boolean;
  popoverRef?: React.RefObject<HTMLDivElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export default function EntityTagPopover({
  title,
  description,
  anchorEl,
  open,
  popoverRef,
  onMouseEnter,
  onMouseLeave,
}: EntityTagPopoverProps): JSX.Element | null {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [ready, setReady] = useState(false);
  const internalRef = useRef<HTMLDivElement | null>(null);

  const setRefs = (node: HTMLDivElement | null) => {
    internalRef.current = node;
    if (popoverRef) {
      const mutableRef =
        popoverRef as React.MutableRefObject<HTMLDivElement | null>;
      mutableRef.current = node;
    }
  };

  useLayoutEffect(() => {
    if (!open) setReady(false);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorEl || !internalRef.current) return;

    const margin = 16;
    const anchorRect = anchorEl.getBoundingClientRect();
    const popoverHeight = internalRef.current.offsetHeight;
    const popoverWidth = internalRef.current.offsetWidth;

    let left = anchorRect.left + window.scrollX;
    const minLeft = window.scrollX + margin;
    const maxLeft = window.scrollX + window.innerWidth - popoverWidth - margin;
    left = Math.max(minLeft, Math.min(left, maxLeft));

    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let top: number;
    if (spaceBelow >= popoverHeight + 8) {
      top = anchorRect.bottom + window.scrollY + 8;
    } else if (spaceAbove >= popoverHeight + 8) {
      top = anchorRect.top + window.scrollY - popoverHeight - 8;
    } else {
      top = anchorRect.bottom + window.scrollY + 8;
    }

    const minTop = window.scrollY + margin;
    const maxTop = window.scrollY + window.innerHeight - popoverHeight - margin;
    top = Math.max(minTop, Math.min(top, maxTop));

    setPosition((prev) => {
      if (prev.top === top && prev.left === left) return prev;
      return { top, left };
    });
    if (!ready) setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorEl, ready]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const handleScroll = () => onMouseLeave?.();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open, onMouseLeave]);

  if (!open) return null;

  return createPortal(
    <Styled.CustomPopover
      ref={setRefs}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: ready ? 'visible' : 'hidden',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Styled.PopoverTitle>{title}</Styled.PopoverTitle>
      {description && (
        <Styled.PopoverContent>{description}</Styled.PopoverContent>
      )}
    </Styled.CustomPopover>,
    document.body
  );
}
